import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import LanguageSelector from './LanguageSelector';
import Turnstile from './Turnstile';
import './EmailConfirmation.css';

const EmailConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'already_confirmed'>('loading');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const hasAttemptedConfirmation = useRef(false);
  const [isExpiredError, setIsExpiredError] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    if (hasAttemptedConfirmation.current) {
      return;
    }
    
    const confirmEmail = async () => {
      hasAttemptedConfirmation.current = true;
      
      const email = searchParams.get('email');
      const token = searchParams.get('token');

      if (!email || !token) {
        setStatus('error');
        setMessage(t('invalidConfirmationLink'));
        return;
      }

      try {
        const response = await apiService.confirmEmail({ email, token });
        
        if (response.success) {
          setStatus('success');
          setMessage(t('yourEmailHasBeenConfirmed'));
          
          // Start countdown for redirect
          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate('/login', { 
                  state: { message: t('emailConfirmedLoginMessage') }
                });
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return () => clearInterval(timer);
        } else {
          // Check if this is an "already confirmed" response
          const responseMessage = response.message || '';
          
          if (response.errors && Array.isArray(response.errors)) {
            // Check for error code 2 (already confirmed)
            if (response.errors.includes('2')) {
              // Email already confirmed - separate status
              setStatus('already_confirmed');
              setMessage(t('emailAlreadyConfirmed'));
              setIsExpiredError(false);
            } else if (response.errors.includes('4')) {
              setStatus('error');
              setMessage(t('invalidOrExpiredLink'));
              setIsExpiredError(true);
            } else {
              setStatus('error');
              setMessage(responseMessage || t('emailConfirmationFailed'));
              setIsExpiredError(false);
            }
          } else if (responseMessage.toLowerCase().includes('already confirmed') || 
                     responseMessage.toLowerCase().includes('already verified')) {
            // Fallback: check message content for "already confirmed"
            setStatus('already_confirmed');
            setMessage(t('emailAlreadyConfirmed'));
            setIsExpiredError(false);
          } else {
            setStatus('error');
            setMessage(responseMessage || t('emailConfirmationFailed'));
            setIsExpiredError(false);
          }
        }
      } catch (error: any) {
        setStatus('error');
        
        // Check for specific error codes from backend
        const errorCodes = error.response?.data?.errors;
        if (errorCodes && Array.isArray(errorCodes)) {
          // Check for error code 2 (already confirmed)
          if (errorCodes.includes('2')) {
            // Email already confirmed - separate status
            setStatus('already_confirmed');
            setMessage(t('emailAlreadyConfirmed'));
            setIsExpiredError(false);
          } else if (errorCodes.includes('4')) {
            setMessage(t('invalidOrExpiredLink'));
            setIsExpiredError(true);
          } else {
            // For error codes 1, 3, or any other error codes, use backend message
            setMessage(error.response?.data?.message || t('errorDuringConfirmation'));
            setIsExpiredError(false);
          }
        } else if (error.response?.data?.message) {
          setMessage(error.response.data.message);
          setIsExpiredError(false);
        } else if (error.response?.status === 400) {
          setMessage(t('invalidOrExpiredLink'));
          setIsExpiredError(true);
        } else {
          setMessage(t('errorDuringConfirmation'));
          setIsExpiredError(false);
        }
      }
    };

    confirmEmail();
  }, [searchParams, navigate, t]);

  const handleReturnToLogin = () => {
    navigate('/login');
  };

  const handleResendEmail = async () => {
    const email = searchParams.get('email');
    if (!email) {
      navigate('/login');
      return;
    }

    setIsResending(true);
    setResendMessage('');
    setResendSuccess(false);

    try {
      const response = await apiService.resendVerificationEmail({
        email,
        turnstileToken: turnstileToken || undefined
      });

      if (response.success) {
        setResendSuccess(true);
        setResendMessage(t('verificationEmailResent'));
        setShowTurnstile(false);
        setTurnstileToken(null);
      } else {
        setResendMessage(response.message || t('failedToResendEmail'));
      }
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      
      // Handle specific error codes based on backend specification
      if (error.response?.data?.errors) {
        const errorCodes = error.response.data.errors;
        if (errorCodes.includes('USER_NOT_FOUND')) {
          setResendMessage(t('noAccountFoundWithEmail'));
        } else if (errorCodes.includes('EMAIL_ALREADY_CONFIRMED')) {
          setResendMessage(t('emailAlreadyVerifiedCanLogin'));
        } else if (errorCodes.includes('ACCOUNT_INACTIVE')) {
          setResendMessage(t('accountDisabledContactSupport'));
        } else if (errorCodes.includes('SECURITY_VERIFICATION_FAILED')) {
          setResendMessage(t('securityCheckFailedTryAgain'));
          setShowTurnstile(true);
        } else if (errorCodes.includes('VALIDATION_ERROR')) {
          setResendMessage(t('invalidInputValidation'));
        } else if (errorCodes.includes('SERVER_ERROR')) {
          setResendMessage(t('somethingWentWrongTryAgainLater'));
        } else {
          setResendMessage(error.response?.data?.message || t('failedToResendEmail'));
        }
      } else if (error.response?.status === 429) {
        // Rate limit error - show exact message from API
        setResendMessage(error.response?.data?.message || t('rateLimitExceeded'));
      } else {
        setResendMessage(error.response?.data?.message || t('failedToResendEmail'));
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    setShowTurnstile(false);
    // Automatically retry resend after getting turnstile token
    setTimeout(() => {
      handleResendEmail();
    }, 100);
  };

  return (
    <div className="email-confirmation-container">
      <div className="email-confirmation-card">
        <div className="confirmation-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span></span>
            <LanguageSelector />
          </div>
          <div className="logo-section">
            <img 
              src="/reforged-300x97.png" 
              alt="Reforged Gym" 
              className="logo-image"
            />
          </div>
          <h1>{t('emailConfirmation')}</h1>
        </div>

        <div className="confirmation-content">
          {status === 'loading' && (
            <div className="loading-section">
              <div className="loading-spinner"></div>
              <h2>{t('confirmingYourEmail')}</h2>
              <p>{t('pleaseWaitWhileWeVerify')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="success-section">
              <div className="success-icon">✓</div>
              <h2>{t('emailConfirmedSuccessfully')}</h2>
              <p>{message}</p>
              <div className="redirect-info">
                <p>{t('youWillBeRedirected')} <strong>{countdown}</strong> {t('seconds')}.</p>
              </div>
              <button 
                onClick={handleReturnToLogin}
                className="login-button"
              >
                {t('goToLoginNow')}
              </button>
            </div>
          )}

          {status === 'already_confirmed' && (
            <div className="success-section">
              <div className="info-icon">ℹ</div>
              <h2>{t('emailConfirmation')}</h2>
              <p>{t('emailAlreadyConfirmed')}</p>
              <button 
                onClick={handleReturnToLogin}
                className="login-button"
              >
                {t('goToLoginNow')}
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="error-section">
              <div className="error-icon">⚠</div>
              <h2>{t('confirmationFailed')}</h2>
              <p>{message}</p>
              
              {isExpiredError && resendMessage && (
                <div className={`resend-message ${resendSuccess ? 'success' : 'error'}`}>
                  {resendMessage}
                </div>
              )}

              {isExpiredError && showTurnstile && (
                <div className="turnstile-container">
                  <p>{t('pleaseCompleteCaptcha')}</p>
                  <Turnstile 
                    onVerify={handleTurnstileSuccess}
                    onError={() => setShowTurnstile(false)}
                  />
                </div>
              )}
              
              <div className="error-actions">
                <button 
                  onClick={handleReturnToLogin}
                  className="login-button"
                >
                  {t('returnToLogin')}
                </button>
                {isExpiredError && !resendSuccess && (
                  <button 
                    onClick={handleResendEmail}
                    className="resend-button"
                    disabled={isResending}
                  >
                    {isResending ? t('sending') : t('resendVerificationEmail')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="confirmation-footer">
          <p>{t('needHelp')}</p>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmation; 